import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import { useEcoregionsStore } from './ecoregions.store';
import type {
  EcoregionsIdentifyResponse,
  EcoregionsLayerResponse,
  EcoregionsStatsResponse,
  MultiPolygonCoords,
} from './ecoregions.types';

/**
 * RESOLVE Ecoregions 2017 metadata, statistics, and identify calls are served by the
 * feasibility API. The returned variant tile URLs remain on JWT-protected
 * `/ecoregions/<coloring>/tiles/{z}/{x}/{y}` analytics endpoints (Lambda@Edge verifies
 * the bearer on every tile GET); `ApiService` attaches the token automatically on the
 * API calls, and the tile fetcher routes through the raster service so the bearer rides
 * on the tile requests too.
 *
 * Contract: ~/.claude/contract/260625_ecoregions.md
 */
@Composable()
export class useEcoregionsService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useEcoregionsStore())
  public readonly ecoregionsStore!: ReturnType<typeof useEcoregionsStore>;

  /** Three variant tile URLs + biome/NNH legends + attribution, in one payload. Cached. */
  public async getEcoregionsLayer(
    signal?: AbortSignal,
  ): Promise<ApiResponse<EcoregionsLayerResponse>> {
    const cached = this.ecoregionsStore.getLayer();
    if (cached) {
      return cached;
    }

    const res = await this.api.get<EcoregionsLayerResponse>(
      `${import.meta.env.VITE_FEASIBILITY_API}/ecoregions/layer`,
      { signal },
    );

    if (res.isSuccess && res.data) {
      this.ecoregionsStore.setLayer(res);
    }
    return res;
  }

  /** Polygon stats → ecoregion / biome / realm / NNH area breakdown for the AOI. */
  public async getEcoregionsStats(
    coords: MultiPolygonCoords,
    signal?: AbortSignal,
  ): Promise<ApiResponse<EcoregionsStatsResponse>> {
    return this.api.post<EcoregionsStatsResponse>(
      `${import.meta.env.VITE_FEASIBILITY_API}/ecoregions/stats`,
      { data: coords },
      { signal },
    );
  }

  /**
   * Point identify for click-to-inspect. Returns the raw `ApiResponse` so the caller can
   * branch on `error.status`: `404` (no ecoregion — e.g. open ocean) vs `422` (bad coords)
   * vs success.
   */
  public async identify(
    lon: number,
    lat: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<EcoregionsIdentifyResponse>> {
    return this.api.get<EcoregionsIdentifyResponse>(
      `${import.meta.env.VITE_FEASIBILITY_API}/ecoregions/identify?lon=${lon}&lat=${lat}`,
      { signal },
    );
  }
}
